// E3 — sub-run prompts (PT-BR). Two DISTINCT framings the driver hands to the
// injected `runAgent`:
//   - buildFixPrompt        → direct repair ("o verify falhou, eis a evidência, CONSERTE e re-rode")
//   - buildTroubleshootPrompt → systematic debugging (reproduzir → minimizar → hipótese →
//                               instrumentar → corrigir → regressão)
// Pure string builders: no key, no side effects, no invented numbers. The agent
// is told to re-run the verify with run_command and NEVER to claim a green it
// did not observe.

// Keep the embedded evidence bounded so a huge log never balloons the prompt.
const MAX_EVIDENCE = 4000;

function clip(value) {
  const text = value == null ? '' : String(value);
  if (text.length <= MAX_EVIDENCE) return text;
  // keep the TAIL — the failing assertion / stack is usually at the end.
  return `…(evidência truncada)…\n${text.slice(text.length - MAX_EVIDENCE)}`;
}

/**
 * Prompt for the FIXER sub-run. The verify command failed; here is the evidence;
 * fix the cause and re-run the verify with run_command. No invented results.
 *
 * @param {string} evidence — captured stdout/stderr from the failed verify.
 * @returns {string} a PT-BR task prompt.
 */
export function buildFixPrompt(evidence) {
  return [
    'O comando de verificação do projeto FALHOU após a sua última alteração.',
    '',
    'Evidência (saída real da verificação):',
    '```',
    clip(evidence),
    '```',
    '',
    'Sua tarefa:',
    '1. Leia a evidência acima e identifique a CAUSA real da falha.',
    '2. CONSERTE o código (edite os arquivos necessários). Não contorne nem mascare o erro.',
    '3. Re-rode a MESMA verificação com a ferramenta run_command e confirme que ela passou.',
    '',
    'Regras:',
    '- Não invente resultados. Não afirme que passou sem ter rodado a verificação de verdade.',
    '- Não cite números (de testes, cobertura, tempo) que você não observou na saída real.',
    '- Se não conseguir consertar, explique objetivamente o que tentou e o que ainda falha.',
  ].join('\n');
}

/**
 * Prompt for the TROUBLESHOOTER sub-run. A direct fix already failed once — drop
 * into systematic debugging instead of guessing again.
 *
 * @param {string} evidence — captured stdout/stderr from the latest failed verify.
 * @param {string} [history] — short note on what was already tried (e.g. "o fixer rodou e a verificação seguiu vermelha").
 * @returns {string} a PT-BR task prompt.
 */
export function buildTroubleshootPrompt(evidence, history) {
  const lines = [
    'A verificação CONTINUA falhando mesmo após uma tentativa direta de conserto.',
    'Pare de adivinhar. Aplique depuração sistemática, passo a passo.',
    '',
    'Evidência mais recente (saída real da verificação):',
    '```',
    clip(evidence),
    '```',
  ];

  if (history) {
    lines.push('', `O que já foi tentado: ${String(history)}`);
  }

  lines.push(
    '',
    'Roteiro de depuração sistemática:',
    '1. REPRODUZIR — rode a verificação com run_command e observe a falha exata (mensagem, arquivo, linha).',
    '2. MINIMIZAR — reduza ao menor caso/teste que ainda reproduz o erro; isole o módulo culpado.',
    '3. HIPÓTESE — formule UMA hipótese concreta da causa raiz, baseada na evidência (não num palpite).',
    '4. INSTRUMENTAR — adicione logs/asserts temporários para confirmar ou refutar a hipótese antes de mexer no código.',
    '5. CORRIGIR — aplique a menor mudança que resolve a causa raiz (não o sintoma).',
    '6. REGREDIR — re-rode a verificação completa com run_command; confirme verde e remova a instrumentação temporária.',
    '',
    'Regras:',
    '- Não invente resultados nem números: só relate o que a saída real mostrou.',
    '- Uma hipótese por vez; confirme antes de corrigir.',
    '- Se ao fim ainda falhar, descreva a causa raiz provável e por que o conserto está fora do alcance deste turno.',
  );

  return lines.join('\n');
}
